// biome-ignore-all lint/suspicious/noExplicitAny: dynamic translation keys
import type { ExpiringAgreementModel } from '@kk/api/modules/leasing';
import useBreakpoint from '@kk/shared/hooks/useBreakpoint';
import { formatDate } from '@kk/shared/utils/date';
import { formatCurrency } from '@kk/shared/utils/numbers';
import { InlineNotification } from '@kk/ui/components/Notifications/InlineNotification';
import Spinner from '@kk/ui/components/Spinner';
import {
  createTableColumnHelper,
  type TableColumnDef,
} from '@kk/ui/components/Table';
import Table from '@kk/ui/components/Table/Table';
import Widget from '@kk/ui/components/Widget';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHandledAgreements } from '@/api/hooks/useExpiringAgreements';
import TextLink from '@/components/TextLink/TextLink';

const columnHelper = createTableColumnHelper<ExpiringAgreementModel>();

export default function ExpiringAgreementsHistory() {
  const { t } = useTranslation();
  const isDesktop = useBreakpoint('(min-width: 768px)');

  const {
    data: handledAgreements = [],
    isLoading,
    isError,
  } = useHandledAgreements();

  const historyColumns = useMemo<
    TableColumnDef<ExpiringAgreementModel>[]
  >(() => {
    const desktopColumns = isDesktop
      ? [
          columnHelper.accessor('contractDescription', {
            header: t('common.description'),
            cell: (info) => info.getValue(),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
          // columnHelper.accessor('residualValueInclVAT', {
          //   header: t('common.remainingValueIncludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          // columnHelper.accessor('residualValueExclVAT', {
          //   header: t('common.remainingValueExcludingVAT'),
          //   cell: (info) =>
          //     formatCurrency(info.getValue(), { appendCurrency: true }),
          //   enableColumnFilter: false,
          //   meta: {
          //     rightAlign: true,
          //   },
          // }),
          columnHelper.accessor('residualValueExclVAT', {
            header: t('common.residualValue'),
            cell: (info) => {
              const row = info.row.original;
              const value =
                row.matureCode === 'LEASEPLAN' ? 0 : info.getValue();
              return formatCurrency(value, { appendCurrency: true });
            },
            enableColumnFilter: false,
            meta: {
              rightAlign: true,
            },
          }),
          columnHelper.accessor('periodEnd', {
            header: t('common.expirationDate'),
            cell: (info) => formatDate(info.getValue()),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
          columnHelper.accessor('entryDate', {
            header: t('common.answeredDate'),
            cell: (info) => formatDate(info.getValue()),
            enableColumnFilter: false,
            meta: {
              rightAlign: false,
            },
          }),
        ]
      : [];

    const visibleColumns = [
      columnHelper.accessor('contractNo', {
        header: t('common.agreement'),
        cell: (info) => {
          return (
            <TextLink
              to={`/agreements/leasingagreements/${info.getValue()}`}
              text={info.getValue() ?? ''}
              iconType="loan-alt"
              solid={true}
            />
          );
        },
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
      ...desktopColumns,
      columnHelper.accessor('matureCode', {
        header: t('common.action'),
        cell: (info) =>
          t(`common.expiringAgreements.action.${info.getValue()}` as any),
        enableColumnFilter: false,
        meta: {
          rightAlign: false,
        },
      }),
    ];

    return columnHelper.columns(visibleColumns);
  }, [t, isDesktop]);

  return (
    <div className="p-8">
      <Widget
        title={t(`common.agreementsHandled`)}
        icon="archive"
        actions={[
          {
            text: t('button.export'),
            theme: 'default',
            icon: 'arrow-down-underlined',
            disabled: isLoading,
          },
        ]}
        description={t(`expiringAgreements.description.history`)}
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="medium" />
          </div>
        ) : isError ? (
          <InlineNotification
            type="danger"
            size="medium"
            headline={t('error.api.title')}
            message={t('error.api.message')}
          />
        ) : handledAgreements.length ? (
          <Table<ExpiringAgreementModel>
            stickyHeader
            sortable
            variant="relaxed"
            columns={historyColumns}
            data={handledAgreements}
            initialSorting={[{ id: 'entryDate', desc: true }]}
            className="border-neutral-10 bg-neutral-0 kunde:h-auto rounded-md border"
          />
        ) : (
          <p className="typ-body">{t('table.noData.expiringAgreements')}</p>
        )}
      </Widget>
    </div>
  );
}
